export function formatInlineMarkdown(text: string): string {
	const escaped = text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");

	// Handle multi-line code blocks first (before splitting into lines)
	let processedText = escaped.replace(
		/```([\s\S]*?)```/g,
		"<pre style='background-color: #f3f4f6; padding: 0.75rem; border-radius: 0.375rem; margin: 0.5rem 0; overflow-x: auto; font-family: ui-monospace, SFMono-Regular, \"SF Mono\", Monaco, Menlo, Consolas, monospace; font-size: 0.875rem; white-space: pre-wrap; border: 1px solid #e5e7eb;'><code>$1</code></pre>"
	);

	// Handle inline code with single backticks
	processedText = processedText.replace(
		/`([^`]+)`/g,
		"<code style='background-color: #f3f4f6; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: ui-monospace, SFMono-Regular, \"SF Mono\", Monaco, Menlo, Consolas, monospace; font-size: 0.875rem; border: 1px solid #e5e7eb;'>$1</code>"
	);

	// Split by lines to handle line-based formatting
	const lines = processedText.split(/\r?\n/);
	const formattedLines = lines.map((line) => {
		// Skip processing lines that are inside code blocks (already processed)
		if (
			line.includes("<pre style=") ||
			line.includes("</pre>") ||
			line.includes("<code>")
		) {
			return line;
		}

		// Handle headers (###, ##, #)
		if (line.match(/^###\s+(.+)$/)) {
			return line.replace(
				/^###\s+(.+)$/,
				"<h3 style='font-size: 1.125rem; font-weight: 600; margin: 0.75rem 0 0.5rem 0; color: #111827;'>$1</h3>"
			);
		}
		if (line.match(/^##\s+(.+)$/)) {
			return line.replace(
				/^##\s+(.+)$/,
				"<h2 style='font-size: 1.25rem; font-weight: 600; margin: 1rem 0 0.5rem 0; color: #111827;'>$1</h2>"
			);
		}
		if (line.match(/^#\s+(.+)$/)) {
			return line.replace(
				/^#\s+(.+)$/,
				"<h1 style='font-size: 1.5rem; font-weight: 600; margin: 1.25rem 0 0.75rem 0; color: #111827;'>$1</h1>"
			);
		}

		// Handle horizontal dividers (---)
		if (line.match(/^---+$/)) {
			return "<hr style='border: none; border-top: 1px solid #e5e7eb; margin: 1rem 0; opacity: 0.6;' />";
		}

		// Handle dashed lists (- item) with nesting support
		if (line.match(/^(\s*)-\s+(.+)$/)) {
			const match = line.match(/^(\s*)-\s+(.+)$/);
			if (!match) return line;
			const indent = match[1].length; // Count leading spaces
			const content = match[2];

			// Calculate margin-left based on indentation level
			const nestingLevel = Math.floor(indent / 2);
			const marginLeft = nestingLevel * 1.5; // 1.5rem per indentation level

			return `<div style='margin: 0.25rem 0; margin-left: ${marginLeft}rem; display: flex; align-items: flex-start;'><span style='color: #6b7280; margin-right: 0.5rem; font-size: 0.875rem;'>•</span><span style='font-size: 0.875rem;'>${content}</span></div>`;
		}

		// Handle numbered lists (1. item) with nesting support
		if (line.match(/^(\s*)(\d+)\.\s+(.+)$/)) {
			const match = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
			if (!match) return line;
			const indent = match[1].length;
			const number = match[2];
			const content = match[3];

			const nestingLevel = Math.floor(indent / 2);
			const marginLeft = nestingLevel * 1.5;

			return `<div style='margin: 0.25rem 0; margin-left: ${marginLeft}rem; display: flex; align-items: flex-start;'><span style='color: #6b7280; margin-right: 0.5rem; font-size: 0.875rem; min-width: 1.5rem;'>${number}.</span><span style='font-size: 0.875rem;'>${content}</span></div>`;
		}

		// Handle regular text lines with indentation
		if (line.match(/^(\s+)(.+)$/)) {
			const match = line.match(/^(\s+)(.+)$/);
			if (!match) return line;
			const indent = match[1].length; // Count leading spaces
			const content = match[2];

			// Calculate margin-left based on indentation level (2 spaces = 1 level)
			const nestingLevel = Math.floor(indent / 2);
			const marginLeft = nestingLevel * 1.5; // 1.5rem per indentation level

			return `<div style='margin: 0.25rem 0; margin-left: ${marginLeft}rem; font-size: 0.875rem;'>${content}</div>`;
		}

		return line;
	});

	// Join lines with selective line breaks
	const joinedText = formattedLines
		.map((line, index) => {
			const nextLine = formattedLines[index + 1];

			// Check if current line is or contains a heading
			const isHeading =
				line.includes("<h1") ||
				line.includes("<h2") ||
				line.includes("<h3") ||
				line.includes("</h1>") ||
				line.includes("</h2>") ||
				line.includes("</h3>");

			// Don't add <br/> after block elements or before block elements
			const isBlockElement =
				isHeading ||
				line.includes("<hr") ||
				line.includes("<pre") ||
				line.includes("</pre>") ||
				line.includes("<div style=");
			const nextIsBlockElement =
				nextLine &&
				(nextLine.includes("<h1") ||
					nextLine.includes("<h2") ||
					nextLine.includes("<h3") ||
					nextLine.includes("<hr") ||
					nextLine.includes("<pre") ||
					nextLine.includes("</pre>") ||
					nextLine.includes("<div style="));

			// Don't add <br/> if this is a block element, next is a block element, or it's the last line
			if (
				isBlockElement ||
				nextIsBlockElement ||
				index === formattedLines.length - 1
			) {
				return line;
			}

			// Add <br/> for regular text lines
			return line + "<br/>";
		})
		.join("");

	return joinedText
		.replace(/\*\*([\s\S]+?)\*\*/g, "<strong style='font-weight: 600;'>$1</strong>")
		.replace(/\*([\s\S]+?)\*/g, "<em style='font-style: italic;'>$1</em>");
}

export const normalizeNative = (nativeRange: any) => {

	// document.getSelection model has properties startContainer and endContainer
	// shadow.getSelection model has baseNode and focusNode
	// Unify formats to always look like document.getSelection 

	if (nativeRange) {

		const range = nativeRange;

		if (range.baseNode) {
			range.startContainer = nativeRange.baseNode;
			range.endContainer = nativeRange.focusNode;
			range.startOffset = nativeRange.baseOffset;
			range.endOffset = nativeRange.focusOffset;

			if (range.endOffset < range.startOffset) {
				range.startContainer = nativeRange.focusNode;
				range.endContainer = nativeRange.baseNode;
				range.startOffset = nativeRange.focusOffset;
				range.endOffset = nativeRange.baseOffset;
			}
		}

		if (range.startContainer) {

			return {
				start: { node: range.startContainer, offset: range.startOffset },
				end: { node: range.endContainer, offset: range.endOffset },
				native: range
			};
		}
	}

	return null
};